import type {
  DrugUseEvaluationT,
  GetRxChangeDetailedResponseT,
  PrescriptionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { buildRenewalBadge, medicationStatusLabel, prescriptionFieldLabel } from '@/entities/rx';
import { toTableCellText } from '@/shared/lib/table';
import { LabeledFieldGrid } from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import { ChevronRight } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/shadcn/collapsible';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/shadcn/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { statusToneClassName } from '@/shared/ui/statusTone';

import { buildMedicationLabel, buildRxChangeTypeLabel } from '../lib';
import { PRESCRIPTION_DETAIL_FIELDS } from './prescriptionDetailFields';
import { RxChangeApproveForm } from './RxChangeApproveForm';
import { RxChangeChangePatientForm } from './RxChangeChangePatientForm';
import { RxChangeDenyForm } from './RxChangeDenyForm';
import { RxChangeReconcileForm } from './RxChangeReconcileForm';

interface PrescriptionSubsectionProps {
  readonly title: string;
  readonly prescription: PrescriptionT | null | undefined;
  readonly emptyHint: string;
}

function PrescriptionSubsection({ title, prescription, emptyHint }: PrescriptionSubsectionProps) {
  if (prescription === null || prescription === undefined) {
    return (
      <section
        aria-label={title}
        data-testid={`rxchange-section-${title.toLowerCase()}`}
        className="rounded-md border p-3"
      >
        <h4 className="mb-1 text-sm font-semibold">{title}</h4>
        <p className="text-muted-foreground text-xs">{emptyHint}</p>
      </section>
    );
  }

  const status = medicationStatusLabel(prescription.MedicationStatus);
  const renewal = buildRenewalBadge(prescription);
  const displayName = toTableCellText(prescription.DisplayName);

  return (
    <section
      aria-label={title}
      data-testid={`rxchange-section-${title.toLowerCase()}`}
      className="rounded-md border p-3"
    >
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <span className="truncate text-sm font-medium">{displayName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {renewal.show ? (
            <Badge className={statusToneClassName('info')} aria-label="Renewal request">
              {renewal.label}
            </Badge>
          ) : null}
          <Badge className={statusToneClassName(status.tone)}>{status.label}</Badge>
        </div>
      </header>

      <LabeledFieldGrid
        items={PRESCRIPTION_DETAIL_FIELDS.map((key) => ({
          label: prescriptionFieldLabel(key),
          value: toTableCellText(prescription[key]),
        }))}
      />
    </section>
  );
}

interface RequestedMedicationsSectionProps {
  readonly medications: ReadonlyArray<PrescriptionT> | null | undefined;
}

function RequestedMedicationsSection({ medications }: RequestedMedicationsSectionProps) {
  if (medications === null || medications === undefined || medications.length === 0) {
    return (
      <section
        aria-label="Requested medications"
        data-testid="rxchange-section-requested"
        className="rounded-md border p-3"
      >
        <h4 className="mb-1 text-sm font-semibold">Requested medications</h4>
        <p className="text-muted-foreground text-xs">
          The pharmacy did not return any specific requested medications for this change.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Requested medications"
      data-testid="rxchange-section-requested"
      className="rounded-md border p-3"
    >
      <h4 className="mb-2 text-sm font-semibold">Requested medications</h4>
      <ul className="flex flex-col gap-2">
        {medications.map((medication, index) => {
          const status = medicationStatusLabel(medication.MedicationStatus);
          return (
            <li
              key={`requested-${index}-${toTableCellText(medication.DisplayName)}`}
              className="flex flex-col gap-1 rounded-md border p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">
                  {toTableCellText(medication.DisplayName)}
                </span>
                <Badge className={statusToneClassName(status.tone)}>{status.label}</Badge>
              </div>
              <div className="text-muted-foreground flex flex-wrap gap-x-3 text-xs">
                <span>Quantity {toTableCellText(medication.Quantity)}</span>
                <span>Refills {toTableCellText(medication.Refills)}</span>
                <span>Days supply {toTableCellText(medication.DaysSupply)}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface DrugUseEvaluationsSectionProps {
  readonly evaluations: ReadonlyArray<DrugUseEvaluationT> | null | undefined;
}

function DrugUseEvaluationsSection({ evaluations }: DrugUseEvaluationsSectionProps) {
  if (evaluations === null || evaluations === undefined || evaluations.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Drug use evaluations"
      data-testid="rxchange-section-due"
      className="rounded-md border p-3"
    >
      <h4 className="mb-2 text-sm font-semibold">Drug use evaluations</h4>
      <ul className="flex flex-col gap-2">
        {evaluations.map((due, index) => (
          <li
            key={`due-${index}-${toTableCellText(due.Service)}`}
            className="flex flex-col gap-1 rounded-md border p-2 text-xs"
          >
            <div className="flex flex-wrap gap-x-3">
              <span className="font-medium">{toTableCellText(due.Service)}</span>
              <span className="text-muted-foreground">Result: {toTableCellText(due.Result)}</span>
              <span className="text-muted-foreground">
                Significance: {toTableCellText(due.Significance)}
              </span>
            </div>
            {due.Reason !== null && due.Reason !== undefined && due.Reason !== '' ? (
              <p className="text-muted-foreground">Reason: {toTableCellText(due.Reason)}</p>
            ) : null}
            {due.AckReason !== null && due.AckReason !== undefined && due.AckReason !== '' ? (
              <p className="text-muted-foreground">Ack: {toTableCellText(due.AckReason)}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface RxChangeDetailSheetProps {
  readonly rxChange: GetRxChangeDetailedResponseT | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function RxChangeDetailSheet({ rxChange, open, onOpenChange }: RxChangeDetailSheetProps) {
  const medicationLabel = buildMedicationLabel(rxChange?.PrescribedPrescription);
  const rxChangeIdLabel = toTableCellText(rxChange?.RxChangeRequestId);
  const typeLabel = buildRxChangeTypeLabel(rxChange?.RxChangeType, rxChange?.RxChangeSubType);
  const sourceLabel = toTableCellText(rxChange?.Source);
  const requestedDateLabel = toTableCellText(rxChange?.RequestedDate);
  const pharmacyLabel = toTableCellText(rxChange?.PharmacyId);
  const payerLabel = toTableCellText(rxChange?.PayerName);
  // ``RxChangeRequestId`` is ``number | null | undefined`` on the SDK type —
  // only mount action forms when we have a usable identifier to send back.
  const actionableRxChangeId =
    typeof rxChange?.RxChangeRequestId === 'number' ? rxChange.RxChangeRequestId : null;
  const closeSheet = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>RxChange #{rxChangeIdLabel}</SheetTitle>
          <SheetDescription>
            <span data-testid="rxchange-type">{typeLabel}</span>
            {' · '}
            <span>Source {sourceLabel}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-4 pb-4">
          <LabeledFieldGrid
            className="mb-3"
            items={[
              {
                label: 'Prescribed',
                value: <span className="truncate">{medicationLabel}</span>,
              },
              { label: 'Requested', value: requestedDateLabel },
              { label: 'Pharmacy', value: pharmacyLabel },
              { label: 'Payer', value: payerLabel },
            ]}
          />

          {rxChange !== null ? (
            <div className="flex flex-col gap-3">
              <PrescriptionSubsection
                title="Prescribed"
                prescription={rxChange.PrescribedPrescription}
                emptyHint="DoseSpot did not return the prescribed prescription."
              />
              <RequestedMedicationsSection medications={rxChange.RequestedMedications} />
              <PrescriptionSubsection
                title="Original"
                prescription={rxChange.OriginalPrescription}
                emptyHint="DoseSpot did not return the original prescription."
              />
              <DrugUseEvaluationsSection evaluations={rxChange.DrugUseEvaluations} />
            </div>
          ) : null}

          {actionableRxChangeId !== null ? (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="approve">
                <TabsList aria-label="RxChange actions">
                  <TabsTrigger value="approve">Approve</TabsTrigger>
                  <TabsTrigger value="deny">Deny</TabsTrigger>
                </TabsList>
                <TabsContent value="approve" className="mt-3">
                  {/* ``key`` resets form state when the user picks a different RxChange. */}
                  <RxChangeApproveForm
                    key={`approve-${actionableRxChangeId}`}
                    rxChangeId={actionableRxChangeId}
                    onCompleted={closeSheet}
                  />
                </TabsContent>
                <TabsContent value="deny" className="mt-3">
                  <RxChangeDenyForm
                    key={`deny-${actionableRxChangeId}`}
                    rxChangeId={actionableRxChangeId}
                    onCompleted={closeSheet}
                  />
                </TabsContent>
              </Tabs>

              {/* Recovery actions — rare; default-collapsed to keep the
                  primary approve/deny path uncluttered. */}
              <Collapsible className="mt-4">
                <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left text-sm font-medium">
                  <ChevronRight
                    aria-hidden="true"
                    className="size-4 transition-transform group-data-[state=open]:rotate-90"
                  />
                  Triage
                  <span className="text-muted-foreground text-xs font-normal">
                    Reconcile or re-route
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 flex flex-col gap-4">
                  <section
                    aria-label="Reconcile to existing prescription"
                    className="flex flex-col gap-2 rounded-md border p-3"
                  >
                    <h5 className="text-sm font-semibold">Reconcile to prescription</h5>
                    <p className="text-muted-foreground text-xs">
                      Attach this RxChange to an existing prescription on the patient&apos;s record
                      when DoseSpot couldn&apos;t auto-link it.
                    </p>
                    <RxChangeReconcileForm
                      key={`reconcile-${actionableRxChangeId}`}
                      rxChangeId={actionableRxChangeId}
                      onCompleted={closeSheet}
                    />
                  </section>
                  <section
                    aria-label="Change patient on this RxChange"
                    className="flex flex-col gap-2 rounded-md border p-3"
                  >
                    <h5 className="text-sm font-semibold">Change patient</h5>
                    <p className="text-muted-foreground text-xs">
                      Re-route this RxChange to a different patient when it was misrouted.
                    </p>
                    <RxChangeChangePatientForm
                      key={`change-patient-${actionableRxChangeId}`}
                      rxChangeId={actionableRxChangeId}
                      onCompleted={closeSheet}
                    />
                  </section>
                </CollapsibleContent>
              </Collapsible>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
